// useNewsCardController — wires the shared `intel.openNewsCard` window event
// into local state. Both the high-severity map markers (W2-B) and the news
// side panel rows (W2-C) dispatch the same event; the listener in the
// MapView wraps both entry points behind one render path.
//
// Event payload contract:
//   detail: {
//     item: NewsItem,
//     position?: { x: number; y: number },  // viewport px — caller's anchor
//   }
//
// When `position` is omitted (the side panel doesn't have a pixel anchor),
// the controller centers the card near the top-right of the map viewport.

import { NewsItem as NewsItemSchema } from '@eidollon/intel-feed-client';
import type { NewsItem } from '@eidollon/intel-feed-client';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

const OpenEventDetail = z.object({
  item: NewsItemSchema,
  position: z.object({ x: z.number().finite(), y: z.number().finite() }).optional(),
});

export interface NewsCardState {
  item: NewsItem;
  position: { x: number; y: number };
}

export function useNewsCardController(): {
  card: NewsCardState | null;
  open: (item: NewsItem, position?: { x: number; y: number }) => void;
  close: () => void;
} {
  const [card, setCard] = useState<NewsCardState | null>(null);

  const open = useCallback((item: NewsItem, position?: { x: number; y: number }) => {
    const pos = position ?? defaultPosition();
    setCard({ item, position: pos });
  }, []);

  const close = useCallback(() => setCard(null), []);

  useEffect(() => {
    function onOpen(e: Event) {
      const ce = e as CustomEvent<unknown>;
      const parsed = OpenEventDetail.safeParse(ce.detail);
      if (!parsed.success) return;
      open(parsed.data.item, parsed.data.position);
    }
    window.addEventListener('intel.openNewsCard', onOpen);
    return () => window.removeEventListener('intel.openNewsCard', onOpen);
  }, [open]);

  return { card, open, close };
}

/**
 * Side-panel entry point doesn't have a marker anchor. Place the card near
 * the top-right of the viewport, biased away from the right edge so the AI
 * rail (when expanded) doesn't sit on top of it.
 */
function defaultPosition(): { x: number; y: number } {
  if (typeof window === 'undefined') return { x: 80, y: 80 };
  const x = Math.max(80, window.innerWidth - 520);
  return { x, y: 100 };
}

/** Dispatch helper used by the side panel + the map. */
export function dispatchOpenNewsCard(item: NewsItem, position?: { x: number; y: number }): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('intel.openNewsCard', { detail: position ? { item, position } : { item } }),
  );
}
